import React, { useState, useEffect, useLayoutEffect } from "react";
import "./portrait.css";
import * as Utils from "../../Utils";
import { getFromIndexedDB } from "../../services/indexedDb";
import longhorizontal from "../../assets/longhorizontal.svg";
import longvertical from "../../assets/longvertical.svg";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow, { tableRowClasses } from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";

let allLayers = [];

const StyledTableRow = styled(TableRow)(() => ({
  [`&.${tableRowClasses.root}`]: {
    height: "28px",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    fontWeight: "bold",
    fontSize: 10,
    padding: "4px 8px",
    borderBottom: "1px solid #ccc",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 10,
    padding: "4px 8px",
    borderBottom: "1px solid #eee",
  },
}));

export const NewPortraitPrintout = () => {
  const [isLoading, setLoading] = useState(true);
  const [imgArray, setImgArray] = useState([]);
  const [overheadImgArray, setOverheadImgArray] = useState([]);
  const [levels, setLevels] = useState(0);

  const queryString = window.location.hash.split("?")[1] || "";
  const urlParams = new URLSearchParams(queryString);
  const data = decodeURIComponent(urlParams.get("data") || "");
  const container = data.split(":");
  const storagetype = container[0] || "ISU-90";
  const containerID = Number(container[1]) || 0;
  const quadid = Number(container[2]) || 0;
 
  const handleClearStates = (e) => {
  e.preventDefault();
  allLayers.length = 0;

  // Option A: Force hash change explicitly
  window.location.hash = "/finalPackingPage";

  // Option B: Fallback to full reload/redirect if SPA router isn't catching hash changes
  // window.location.assign(window.location.origin + "/#/finalPackingPage");
    };

  async function addImages() {
    try {
      const imagesArray = await getFromIndexedDB("imagesArray");
      if (Array.isArray(imagesArray)) setImgArray(imagesArray);

      const overheadImagesArray = await getFromIndexedDB("overheadImagesArray");
      if (Array.isArray(overheadImagesArray)) setOverheadImgArray(overheadImagesArray);
    } catch (err) {
      console.error("Failed to load images from IndexedDB:", err);
    }
  }

  async function waitForSetContainers() {
    const max = await getLevels(storagetype, containerID, quadid);
    setLevels(max);

    if (allLayers.length < max) {
      for (let x = 1; x <= max; x++) {
        await getLayer(max, storagetype, containerID, quadid, x);
      }
    }
    setLoading(false);
  }

  async function getLayer(max, type, palletid, quadNo, levelNo) {
    let result = await Utils.returnPlaceBoxesByLayer(type, palletid, quadNo, levelNo);
    if (result && result.length !== 0) {
      allLayers[levelNo] = { type, palletid, quadNo, levelNo, result };
    }
  }

  async function getLevels(type, palletid, quadNo) {
    return await Utils.returnPlaceBoxesMaxLayer(type, palletid, quadNo);
  }

  function getQuadrantName(id) {
    const names = { 1: "ALPHA", 2: "BRAVO", 3: "CHARLIE", 4: "DELTA" };
    return names[id] || `QUADRANT 0${id}`;
  }

  function roationImage(width, length) {
    const numericWidth = parseFloat(width);
    const numericLength = parseFloat(length);
    const isHorizontal = numericWidth >= numericLength;
    return (
      <img
        src={isHorizontal ? longhorizontal : longvertical}
        className="icon-symbol"
        alt="orientation"
      />
    );
  }

  function renderTable(levelNo) {
    let rows =
      allLayers[levelNo]?.result
        ?.slice()
        .sort((a, b) => a.position_no - b.position_no) || [];

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        className="step-table-container"
      >
        <Table
          size="small"
          aria-label="layer table"
          sx={{
            // Reduces overall text size for the table
            "& .MuiTableCell-root": {
              fontSize: "0.55rem",
              padding: "6px 8px", // Adjust padding for a tighter fit
            },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">NIIN</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "20%" }}>
                Kit
              </StyledTableCell>
              <StyledTableCell align="center">Location</StyledTableCell>
              <StyledTableCell align="center">Position</StyledTableCell>
              <StyledTableCell align="center">Shape</StyledTableCell>
              <StyledTableCell align="center">Dir</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: '8%' }}>Shelf</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.nsn || row.position_no}>
                <StyledTableCell align="center">
                  {String(row.nsn || "").slice(-4)}
                </StyledTableCell>
                <StyledTableCell align="center">{row.ksn}</StyledTableCell>
                <StyledTableCell align="center">{row.location}</StyledTableCell>
                <StyledTableCell align="center">
                  #{row.position_no}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <img
                    className="icon-symbol"
                    src={require("../../assets/retangularprism.png")}
                    alt="prism"
                  />
                </StyledTableCell>
                <StyledTableCell align="center">
                  {roationImage(row.width, row.length)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {row.niin === 9999 && <SpaceDashboardIcon fontSize="small" />}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  useLayoutEffect(() => {
    addImages();
    waitForSetContainers();
  }, []);

  if (isLoading) {
    return <div className="loading-screen">Loading packing pages...</div>;
  }

  const hasNoBoxes =
    levels === 0 ||
    !Array.isArray(allLayers) ||
    !allLayers.some((layer) => Array.isArray(layer?.result) && layer.result.length > 0);

  if (hasNoBoxes) {
    return (
      <div className="no-boxes-container">
        <Button
          className="backbutton-sticky"
          variant="contained"
          onClick={handleClearStates}
        >
          Back
        </Button>
        <div className="no-boxes-card">
          <h2>No boxes</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="print-root">
      <Button
        className="backbutton-sticky"
        variant="contained"
        onClick={handleClearStates}
      >
        Back
      </Button>

      {Array.from({ length: levels }, (_, index) => {
        const levelNo = index + 1;
        const pageNumber = levelNo;

        return (
          <div key={levelNo} className="print-page-portrait">
            {/* Header Section */}
            <header className="page-header">
              <div className="header-title-row">
                <h1 className="doc-main-title">
                  {storagetype} {containerID}{" "}
                  <span className="doc-subtitle">
                    {storagetype === "ISU-90"
                      ? `QUADRANT ${getQuadrantName(quadid)}`
                      : `PALLET 0${containerID}`}
                  </span>
                </h1>
                <div className="page-counter">
                  PAGE {pageNumber}/{levels}
                </div>
              </div>

              <p className="instruction-text">
                1- Find the boxes for each door by identifying last 4 digits of
                NIIN. | 2- For each box, look at the icons (see key at the
                bottom of the page) to guide you in placing them. One icon
                indicates the longest side of the box, helping you orient it
                correctly. | 3- Put each box into the correct position on each
                layer.
              </p>
            </header>

            {/* Main Content Viewport */}
            <main className="page-body">
              <div className="top-content-row">
                <div className="table-wrapper">{renderTable(levelNo)}</div>

                <div className="center-diagram-wrapper">
                  {imgArray[levelNo - 1] ? (
                    <img
                      className={`main-isometric-img ${
                        storagetype === "ISU-90"
                          ? "isu-diagram"
                          : "pallet-diagram"
                      }`}
                      src={imgArray[levelNo - 1]}
                      alt={`Step ${levelNo}`}
                    />
                  ) : (
                    <div className="placeholder-box">
                      Level {levelNo} Diagram
                    </div>
                  )}
                  {storagetype === "ISU-90" && (
                    <div className="door-opening-label">Door</div>
                  )}
                </div>
              </div>

              {/* Bottom Section: Side-by-side Views */}
              <div className="bottom-views-container">
                <div className="view-card">
                  <div className="view-card-title">FULL PACK VIEW</div>
                  <div className="view-card-content">
                    {imgArray[imgArray.length - 1] ? (
                      <img
                        className={`card-img ${
                          storagetype === "ISU-90"
                            ? "isu-card-img"
                            : "pallet-card-img"
                        }`}
                        src={imgArray[imgArray.length - 1]}
                        alt="Full Pack View"
                      />
                    ) : (
                      <div className="placeholder-box">Full Pack</div>
                    )}
                  </div>
                </div>

                <div className="view-card">
                  <div className="view-card-title">UPPER VIEW PER LEVEL</div>
                  <div className="view-card-content">
                    {overheadImgArray[levelNo - 1] ? (
                      <img
                        className={`card-img-1 ${
                          storagetype === "ISU-90"
                            ? "isu-card-img"
                            : "pallet-card-img"
                        }`}
                        src={overheadImgArray[levelNo - 1]}
                        alt="Upper View"
                      />
                    ) : (
                      <div className="level-number-fallback">{levelNo}</div>
                    )}
                  </div>
                </div>
              </div>
            </main>

            {/* Standard Footer Guide */}
            <footer className="page-footer">
              <span className="footer-label">GUIDE:</span>
              <div className="footer-key-item">
                <span className="bold">#N</span> positioning order
              </div>
              <div className="footer-key-item">
                <img
                  src={longhorizontal}
                  alt="longest side"
                  className="footer-key-icon"
                />
                <span>longest side direction</span>
              </div>
              <div className="footer-key-item">
                <img
                  src={require("../../assets/retangularprism.png")}
                  alt="box shape"
                  className="footer-key-icon"
                />
                <span>box shape</span>
              </div>
              <div className="footer-key-item">
                <SpaceDashboardIcon
                  className="footer-key-icon"
                  fontSize="small"
                />
                <span>Shelf Location</span>
              </div>
            </footer>
          </div>
        );
      })}
    </div>
  );
};

export default NewPortraitPrintout;